package com.mgacreative.hotel.feature.admin.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material.icons.outlined.Badge
import androidx.compose.material.icons.outlined.Person
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotel.shared.ui.designsystem.components.HFLoadingState
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AdminMembersScreen(
    onNavigateBack: () -> Unit = {}
) {
    val viewModel = koinViewModel<AdminMembersViewModel>()
    val uiState by viewModel.uiState.collectAsState()
    val actionMessage by viewModel.actionMessage.collectAsState()
    val isSaving by viewModel.isSaving.collectAsState()
    val snackbarHostState = remember { SnackbarHostState() }

    LaunchedEffect(actionMessage) {
        actionMessage?.let { msg ->
            snackbarHostState.showSnackbar(msg)
            viewModel.clearActionMessage()
        }
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        containerColor = Color(0xFFF8FAFC)
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .padding(horizontal = 24.dp, vertical = 16.dp)
        ) {
            // 1. Üst Başlık Çubuğu
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = 16.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(10.dp)
                ) {
                    IconButton(
                        onClick = onNavigateBack,
                        modifier = Modifier.size(36.dp)
                    ) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Geri",
                            tint = Color(0xFF1E293B)
                        )
                    }

                    Box(
                        modifier = Modifier
                            .size(36.dp)
                            .clip(RoundedCornerShape(8.dp))
                            .background(Color(0xFFFEF3C7)),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            imageVector = Icons.Outlined.Badge,
                            contentDescription = null,
                            tint = Color(0xFFD97706),
                            modifier = Modifier.size(20.dp)
                        )
                    }

                    Text(
                        text = "Üye Yönetim Paneli",
                        fontSize = 20.sp,
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFF0F172A)
                    )
                }

                IconButton(
                    onClick = { viewModel.loadMembers() },
                    modifier = Modifier.size(36.dp)
                ) {
                    Icon(
                        imageVector = Icons.Default.Refresh,
                        contentDescription = "Yenile",
                        tint = Color(0xFF475569)
                    )
                }
            }

            // 2. Arama Çubuğu
            val currentQuery = (uiState as? MemberListUiState.Success)?.searchQuery ?: ""
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(44.dp)
                    .background(Color.White, RoundedCornerShape(12.dp))
                    .border(BorderStroke(1.dp, Color(0xFFE2E8F0)), RoundedCornerShape(12.dp))
                    .padding(horizontal = 14.dp),
                contentAlignment = Alignment.CenterStart
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        imageVector = Icons.Default.Search,
                        contentDescription = null,
                        tint = Color(0xFF94A3B8),
                        modifier = Modifier.size(20.dp)
                    )
                    Spacer(Modifier.width(10.dp))
                    Box(modifier = Modifier.weight(1f), contentAlignment = Alignment.CenterStart) {
                        if (currentQuery.isEmpty()) {
                            Text(
                                text = "İsim, e-posta veya üye kodu ara...",
                                color = Color(0xFF94A3B8),
                                fontSize = 14.sp
                            )
                        }
                        BasicTextField(
                            value = currentQuery,
                            onValueChange = viewModel::onSearchQueryChange,
                            singleLine = true,
                            textStyle = TextStyle(color = Color(0xFF0F172A), fontSize = 14.sp),
                            cursorBrush = SolidColor(Color(0xFF0F172A)),
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                    if (currentQuery.isNotEmpty()) {
                        IconButton(
                            onClick = { viewModel.onSearchQueryChange("") },
                            modifier = Modifier.size(24.dp)
                        ) {
                            Icon(
                                imageVector = Icons.Default.Close,
                                contentDescription = "Temizle",
                                tint = Color(0xFF94A3B8),
                                modifier = Modifier.size(16.dp)
                            )
                        }
                    }
                }
            }

            Spacer(Modifier.height(16.dp))

            // 3. Filtre Butonları (Tümü, Aktif, Pasif/Kısıtlı)
            val successState = uiState as? MemberListUiState.Success
            val selectedStatus = successState?.selectedStatus
            val totalCount = successState?.totalCount ?: 0
            val activeCount = successState?.activeCount ?: 0
            val passiveCount = successState?.passiveCount ?: 0

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                FilterTabButton(
                    text = "Tümü ($totalCount)",
                    isSelected = selectedStatus == null,
                    onClick = { viewModel.onFilterStatusChange(null) }
                )
                FilterTabButton(
                    text = "Aktif ($activeCount)",
                    isSelected = selectedStatus == true,
                    onClick = { viewModel.onFilterStatusChange(true) }
                )
                FilterTabButton(
                    text = "Pasif / Kısıtlı ($passiveCount)",
                    isSelected = selectedStatus == false,
                    onClick = { viewModel.onFilterStatusChange(false) }
                )
            }

            Spacer(Modifier.height(16.dp))

            // 4. Liste veya Yükleniyor/Hata Durumu
            when (val state = uiState) {
                is MemberListUiState.Loading -> {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        HFLoadingState()
                    }
                }
                is MemberListUiState.Error -> {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(text = "Hata: ${state.message}", color = MaterialTheme.colorScheme.error)
                            Spacer(Modifier.height(8.dp))
                            Button(onClick = { viewModel.loadMembers() }) {
                                Text("Tekrar Dene")
                            }
                        }
                    }
                }
                is MemberListUiState.Success -> {
                    val filtered = state.filteredMembers
                    if (filtered.isEmpty()) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(top = 40.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                text = "Eşleşen üye kaydı bulunamadı.",
                                color = Color(0xFF64748B),
                                fontSize = 14.sp
                            )
                        }
                    } else {
                        LazyColumn(
                            modifier = Modifier.fillMaxSize(),
                            verticalArrangement = Arrangement.spacedBy(10.dp)
                        ) {
                            items(filtered, key = { it.id }) { member ->
                                MemberRowItem(
                                    member = member,
                                    onSave = { updated -> viewModel.updateMember(updated) },
                                    onToggleStatus = { viewModel.toggleMemberStatus(it) }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FilterTabButton(
    text: String,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    Surface(
        onClick = onClick,
        shape = RoundedCornerShape(8.dp),
        color = if (isSelected) Color(0xFF0F172A) else Color.White,
        border = BorderStroke(1.dp, if (isSelected) Color(0xFF0F172A) else Color(0xFFE2E8F0)),
        modifier = Modifier.height(36.dp)
    ) {
        Box(
            modifier = Modifier.padding(horizontal = 14.dp),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = text,
                color = if (isSelected) Color.White else Color(0xFF475569),
                fontSize = 13.sp,
                fontWeight = if (isSelected) FontWeight.SemiBold else FontWeight.Medium
            )
        }
    }
}

@Composable
private fun MemberRowItem(
    member: Member,
    onSave: (Member) -> Unit,
    onToggleStatus: (Member) -> Unit
) {
    var code by remember(member.member_code) { mutableStateOf(member.member_code ?: "") }
    var startDate by remember(member.start_date) { mutableStateOf(member.start_date ?: "") }
    var endDate by remember(member.end_date) { mutableStateOf(member.end_date ?: "") }
    var hotelName by remember(member.hotel_name) { mutableStateOf(member.hotel_name ?: "") }
    var phone by remember(member.phone) { mutableStateOf(member.phone ?: "") }
    var email by remember(member.email) { mutableStateOf(member.email) }
    var showDepartmentDialog by remember { mutableStateOf(false) }

    Card(
        shape = RoundedCornerShape(14.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            // 1. ÜST BAŞLIK BARI: Profil / Otel Bilgisi + Durum + Departmanlar ve Kaydet Butonları (Sabit ve %100 Görünür)
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                // Sol Taraf: Avatar + Otel Adı + E-posta + Üye Kodu
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    modifier = Modifier.weight(1f, fill = false)
                ) {
                    Box(
                        modifier = Modifier
                            .size(42.dp)
                            .clip(RoundedCornerShape(10.dp))
                            .background(Color(0xFFEEF2FF)),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            imageVector = Icons.Outlined.Person,
                            contentDescription = null,
                            tint = Color(0xFF4F46E5),
                            modifier = Modifier.size(24.dp)
                        )
                    }

                    Column {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Text(
                                text = hotelName.ifBlank { member.hotel_name?.takeIf { it.isNotBlank() } ?: "Otel Tanımsız" },
                                fontSize = 15.sp,
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFF0F172A)
                            )
                            Surface(
                                shape = RoundedCornerShape(6.dp),
                                color = Color(0xFFF1F5F9),
                                border = BorderStroke(1.dp, Color(0xFFE2E8F0))
                            ) {
                                Text(
                                    text = code.ifBlank { member.member_code ?: "KOD YOK" },
                                    fontSize = 11.sp,
                                    fontWeight = FontWeight.SemiBold,
                                    color = Color(0xFF475569),
                                    modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                                )
                            }
                        }
                        Text(
                            text = email.ifBlank { member.email },
                            fontSize = 12.sp,
                            color = Color(0xFF64748B)
                        )
                    }
                }

                // Sağ Taraf: AKTİF/PASİF + DEPARTMAN YETKİLERİ BUTONU + KAYDET BUTONU
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    // Durum (Aktif / Pasif) Switch
                    Surface(
                        shape = RoundedCornerShape(8.dp),
                        color = if (member.is_active) Color(0xFFECFDF5) else Color(0xFFFEF2F2),
                        border = BorderStroke(1.dp, if (member.is_active) Color(0xFFA7F3D0) else Color(0xFFFECACA))
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp)
                        ) {
                            Switch(
                                checked = member.is_active,
                                onCheckedChange = { onToggleStatus(member) },
                                colors = SwitchDefaults.colors(
                                    checkedThumbColor = Color.White,
                                    checkedTrackColor = Color(0xFF10B981),
                                    uncheckedThumbColor = Color.White,
                                    uncheckedTrackColor = Color(0xFF94A3B8)
                                ),
                                modifier = Modifier.scale(0.75f)
                            )
                            Text(
                                text = if (member.is_active) "AKTİF" else "PASİF",
                                fontSize = 11.sp,
                                fontWeight = FontWeight.Bold,
                                color = if (member.is_active) Color(0xFF047857) else Color(0xFFB91C1C)
                            )
                        }
                    }

                    // DEPARTMANLAR / HİZMETLER YETKİ BUTONU (BÜYÜK VE BELİRGİN)
                    Button(
                        onClick = { showDepartmentDialog = true },
                        shape = RoundedCornerShape(8.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF4F46E5)),
                        contentPadding = PaddingValues(horizontal = 12.dp, vertical = 6.dp),
                        modifier = Modifier.height(36.dp)
                    ) {
                        Icon(
                            imageVector = Icons.Default.Settings,
                            contentDescription = null,
                            tint = Color.White,
                            modifier = Modifier.size(16.dp)
                        )
                        Spacer(Modifier.width(6.dp))
                        Text(
                            text = "Departman Yetkileri (${member.allowed_modules.size}/10)",
                            fontSize = 12.sp,
                            fontWeight = FontWeight.Bold,
                            color = Color.White
                        )
                    }

                    // KAYDET BUTONU
                    Button(
                        onClick = {
                            onSave(
                                member.copy(
                                    member_code = code,
                                    start_date = startDate,
                                    end_date = endDate,
                                    hotel_name = hotelName,
                                    phone = phone,
                                    email = email
                                )
                            )
                        },
                        shape = RoundedCornerShape(8.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF0F172A)),
                        contentPadding = PaddingValues(horizontal = 14.dp, vertical = 6.dp),
                        modifier = Modifier.height(36.dp)
                    ) {
                        Icon(
                            imageVector = Icons.Default.Check,
                            contentDescription = null,
                            tint = Color.White,
                            modifier = Modifier.size(16.dp)
                        )
                        Spacer(Modifier.width(4.dp))
                        Text("Kaydet", fontSize = 12.sp, color = Color.White, fontWeight = FontWeight.SemiBold)
                    }
                }
            }

            // 2. FORM ALANLARI: Ekran Genişliğine Göre Responsive Dağılım (Kaydırma Yok)
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                LabeledInlineTextField(
                    label = "Üyelik Kodu",
                    value = code,
                    onValueChange = { code = it.uppercase() },
                    placeholder = "MBR-...",
                    modifier = Modifier.weight(1f)
                )
                LabeledInlineTextField(
                    label = "Üye Başlangıç",
                    value = startDate,
                    onValueChange = { startDate = it },
                    placeholder = "YYYY-AA-GG",
                    modifier = Modifier.weight(1f)
                )
                LabeledInlineTextField(
                    label = "Üye Bitiş",
                    value = endDate,
                    onValueChange = { endDate = it },
                    placeholder = "YYYY-AA-GG",
                    modifier = Modifier.weight(1f)
                )
                LabeledInlineTextField(
                    label = "Otel Adı",
                    value = hotelName,
                    onValueChange = { hotelName = it },
                    placeholder = "Otel Adı...",
                    modifier = Modifier.weight(1.3f)
                )
                LabeledInlineTextField(
                    label = "Telefon",
                    value = phone,
                    onValueChange = { phone = it },
                    placeholder = "+90...",
                    modifier = Modifier.weight(1f)
                )
                LabeledInlineTextField(
                    label = "e-mail",
                    value = email,
                    onValueChange = { email = it },
                    placeholder = "E-posta",
                    modifier = Modifier.weight(1.4f)
                )
            }

            // 3. KART ÜZERİNDE DOĞRUDAN DEPARTMAN YÖNETİM BAR VE ÇİPLERİ (HIZLI AÇ/KAPA)
            Surface(
                shape = RoundedCornerShape(8.dp),
                color = Color(0xFFF8FAFC),
                border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 10.dp, vertical = 6.dp)
                        .horizontalScroll(rememberScrollState()),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    Text(
                        text = "Departmanlar:",
                        fontSize = 11.sp,
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFF475569),
                        modifier = Modifier.padding(end = 4.dp)
                    )

                    ALL_DEPARTMENTS.forEach { dept ->
                        val isEnabled = member.allowed_modules.contains(dept.key)
                        val chipBg = when {
                            dept.isV2 -> Color(0xFFFEF3C7)
                            isEnabled -> Color(0xFFECFDF5)
                            else -> Color(0xFFF1F5F9)
                        }
                        val chipBorder = when {
                            dept.isV2 -> Color(0xFFFDE68A)
                            isEnabled -> Color(0xFFA7F3D0)
                            else -> Color(0xFFCBD5E1)
                        }
                        val chipText = when {
                            dept.isV2 -> Color(0xFFB45309)
                            isEnabled -> Color(0xFF047857)
                            else -> Color(0xFF64748B)
                        }

                        Surface(
                            shape = RoundedCornerShape(6.dp),
                            color = chipBg,
                            border = BorderStroke(1.dp, chipBorder),
                            modifier = Modifier.clickable(enabled = !dept.isV2) {
                                val currentModules = member.allowed_modules.toMutableList()
                                if (isEnabled) {
                                    currentModules.remove(dept.key)
                                } else {
                                    currentModules.add(dept.key)
                                }
                                onSave(
                                    member.copy(
                                        member_code = code,
                                        start_date = startDate,
                                        end_date = endDate,
                                        hotel_name = hotelName,
                                        phone = phone,
                                        email = email,
                                        allowed_modules = currentModules
                                    )
                                )
                            }
                        ) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp)
                            ) {
                                Text(
                                    text = if (isEnabled) "✓ " else "+ ",
                                    fontSize = 11.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = chipText
                                )
                                Text(
                                    text = dept.title.substringAfter(". "),
                                    fontSize = 11.sp,
                                    fontWeight = if (isEnabled) FontWeight.SemiBold else FontWeight.Normal,
                                    color = chipText
                                )
                                if (dept.isV2) {
                                    Text(
                                        text = " (V2)",
                                        fontSize = 9.sp,
                                        fontWeight = FontWeight.Bold,
                                        color = chipText
                                    )
                                }
                            }
                        }
                    }

                    TextButton(
                        onClick = { showDepartmentDialog = true },
                        contentPadding = PaddingValues(horizontal = 6.dp, vertical = 2.dp)
                    ) {
                        Text(
                            text = "Tümünü Düzenle →",
                            fontSize = 11.sp,
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFF4F46E5)
                        )
                    }
                }
            }
        }
    }

    if (showDepartmentDialog) {
        MemberDepartmentsDialog(
            member = member,
            onDismiss = { showDepartmentDialog = false },
            onSaveModules = { updatedModules ->
                onSave(
                    member.copy(
                        member_code = code,
                        start_date = startDate,
                        end_date = endDate,
                        hotel_name = hotelName,
                        phone = phone,
                        email = email,
                        allowed_modules = updatedModules
                    )
                )
                showDepartmentDialog = false
            }
        )
    }
}

@Composable
private fun LabeledInlineTextField(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String = "",
    modifier: Modifier = Modifier,
    height: Dp = 36.dp
) {
    Column(modifier = modifier) {
        Text(
            text = label,
            fontSize = 10.sp,
            fontWeight = FontWeight.Medium,
            color = Color(0xFF64748B),
            modifier = Modifier.padding(bottom = 3.dp)
        )
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(height)
                .background(Color.White, RoundedCornerShape(8.dp))
                .border(BorderStroke(1.dp, Color(0xFFE2E8F0)), RoundedCornerShape(8.dp))
                .padding(horizontal = 10.dp),
            contentAlignment = Alignment.CenterStart
        ) {
            if (value.isEmpty() && placeholder.isNotEmpty()) {
                Text(
                    text = placeholder,
                    color = Color(0xFF94A3B8),
                    fontSize = 12.sp,
                    maxLines = 1
                )
            }
            BasicTextField(
                value = value,
                onValueChange = onValueChange,
                singleLine = true,
                textStyle = TextStyle(
                    color = Color(0xFF0F172A),
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Medium
                ),
                cursorBrush = SolidColor(Color(0xFF0F172A)),
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

private data class DepartmentDefinition(
    val key: String,
    val title: String,
    val description: String,
    val isDefault: Boolean = false,
    val isV2: Boolean = false
)

private val ALL_DEPARTMENTS = listOf(
    DepartmentDefinition("FRONT_DESK", "1. Ön Büro", "Rezervasyon takvimi, check-in / check-out ve misafir kabul", isDefault = true),
    DepartmentDefinition("ROOM_STATUS", "2. Oda Durumları", "Oda doluluk, kat temizlik ve kat planı şeması", isDefault = true),
    DepartmentDefinition("HOUSEKEEPING", "3. Kat Hizmetleri", "Oda temizlik görevleri, mini bar kontrolü ve çamaşırhane"),
    DepartmentDefinition("ACCOUNTING", "4. Kasa & Finans", "Kasa hareketleri, cari hesaplar, folyo ve finansal raporlar"),
    DepartmentDefinition("RESTAURANT", "5. Restoran POS", "Restoran adisyonları, masa sipariş ve POS terminalleri"),
    DepartmentDefinition("INVENTORY", "6. Envanter Yönetimi", "Depo stokları, tüketim takibi ve tedarik yönetimi"),
    DepartmentDefinition("STAFF", "7. Personel Yönetimi", "Çalışan listesi, vardiya planı ve izin takibi", isDefault = true),
    DepartmentDefinition("CHANNEL_MANAGER", "8. OTA Kanal Yönetimi", "Booking, Airbnb, Expedia bağlantıları ve fiyat senkronizasyonu"),
    DepartmentDefinition("SYSTEM", "9. Sistem Yönetimi", "Admin kontrol merkezi, otel ayarları ve oda konfigürasyonları", isDefault = true),
    DepartmentDefinition("E_INVOICE", "10. E-Fatura Entegrasyonu", "GİB E-Arşiv ve E-Fatura düzenleme", isV2 = true)
)

@Composable
private fun MemberDepartmentsDialog(
    member: Member,
    onDismiss: () -> Unit,
    onSaveModules: (List<String>) -> Unit
) {
    val selectedModules = remember { mutableStateListOf<String>().apply { addAll(member.allowed_modules) } }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Column {
                Text(
                    text = "Departman / Hizmet Yetkileri",
                    fontSize = 18.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFF0F172A)
                )
                Text(
                    text = "${member.hotel_name?.takeIf { it.isNotBlank() } ?: member.full_name ?: member.email} için sol menüde açık olacak hizmetleri seçin.",
                    fontSize = 12.sp,
                    color = Color(0xFF64748B),
                    modifier = Modifier.padding(top = 4.dp)
                )
            }
        },
        text = {
            LazyColumn(
                modifier = Modifier
                    .fillMaxWidth()
                    .heightIn(max = 420.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                items(ALL_DEPARTMENTS, key = { it.key }) { dept ->
                    val isChecked = selectedModules.contains(dept.key)
                    Surface(
                        shape = RoundedCornerShape(10.dp),
                        color = if (isChecked) Color(0xFFF8FAFC) else Color.White,
                        border = BorderStroke(1.dp, if (isChecked) Color(0xFFCBD5E1) else Color(0xFFE2E8F0)),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 12.dp, vertical = 10.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Column(modifier = Modifier.weight(1f).padding(end = 8.dp)) {
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(6.dp)
                                ) {
                                    Text(
                                        text = dept.title,
                                        fontSize = 13.sp,
                                        fontWeight = FontWeight.SemiBold,
                                        color = if (dept.isV2) Color(0xFF94A3B8) else Color(0xFF1E293B)
                                    )
                                    if (dept.isDefault) {
                                        Surface(
                                            color = Color(0xFFE0F2FE),
                                            shape = RoundedCornerShape(4.dp)
                                        ) {
                                            Text(
                                                text = "Varsayılan",
                                                fontSize = 10.sp,
                                                fontWeight = FontWeight.Bold,
                                                color = Color(0xFF0284C7),
                                                modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                                            )
                                        }
                                    }
                                    if (dept.isV2) {
                                        Surface(
                                            color = Color(0xFFFEF3C7),
                                            shape = RoundedCornerShape(4.dp)
                                        ) {
                                            Text(
                                                text = "V2 - Yakında",
                                                fontSize = 10.sp,
                                                fontWeight = FontWeight.Bold,
                                                color = Color(0xFFD97706),
                                                modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                                            )
                                        }
                                    }
                                }
                                Text(
                                    text = dept.description,
                                    fontSize = 11.sp,
                                    color = Color(0xFF64748B),
                                    modifier = Modifier.padding(top = 2.dp)
                                )
                            }

                            Switch(
                                checked = isChecked,
                                enabled = !dept.isV2,
                                onCheckedChange = { checked ->
                                    if (checked) {
                                        if (!selectedModules.contains(dept.key)) selectedModules.add(dept.key)
                                    } else {
                                        selectedModules.remove(dept.key)
                                    }
                                },
                                colors = SwitchDefaults.colors(
                                    checkedThumbColor = Color.White,
                                    checkedTrackColor = Color(0xFF2563EB),
                                    uncheckedThumbColor = Color.White,
                                    uncheckedTrackColor = Color(0xFFCBD5E1)
                                ),
                                modifier = Modifier.scale(0.85f)
                            )
                        }
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = { onSaveModules(selectedModules.toList()) },
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                shape = RoundedCornerShape(8.dp)
            ) {
                Text("Yetkileri Kaydet", fontWeight = FontWeight.SemiBold)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Vazgeç", color = Color(0xFF64748B))
            }
        },
        shape = RoundedCornerShape(16.dp),
        containerColor = Color.White
    )
}
